import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useId,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { useOutsideClick, useMobileScrollLock } from '@/common/hooks';

export function CustomSelect({
  value,
  onChange,
  options = [],
  placeholder,
  disabled,
  isLoading = false,
  className = '',
  textSizeClass = 'text-[14px] font-medium',
  error,
  buttonClassName = '',
  buttonStyle,
  dropdownWidth,
  hideCheckIcon = false,
  hideChevron = false,
  clearable = false,
  onClear,
  renderOption,
  renderValue,
  searchable = false,
  searchPlaceholder = 'Search...',
  multiple = false,
  emptyMessage = 'No options available',
  /** When set, search is server-driven (no local filter). Called with debounced query. */
  onSearchChange,
  /** Called when list scrolled near bottom (infinite load). */
  onLoadMore,
  hasMore = false,
  isFetchingMore = false,
  searchDebounceMs = 300,
  align = 'left',
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy,
}) {
  const instanceId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({
    position: 'fixed',
    top: -9999,
    left: -9999,
  });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const didAutoScrollRef = useRef(false);

  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  useMobileScrollLock(isOpen);

  const serverSideSearch = typeof onSearchChange === 'function';
  const serverSideSearchRef = useRef(serverSideSearch);
  serverSideSearchRef.current = serverSideSearch;

  const filteredOptions = useMemo(() => {
    if (searchable && !serverSideSearch) {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return options;
      return (options || []).filter((opt) =>
        String(opt?.label ?? '')
          .toLowerCase()
          .includes(q),
      );
    }
    return options || [];
  }, [options, searchable, serverSideSearch, searchQuery]);

  useOutsideClick(
    [containerRef, dropdownRef],
    () => {
      setIsOpen(false);
      setSearchQuery('');
      if (serverSideSearchRef.current) onSearchChangeRef.current?.('');
    },
    isOpen,
  );

  useEffect(() => {
    function handleOtherDropdownOpened(event) {
      if (event.detail?.id !== instanceId) {
        setIsOpen(false);
        setSearchQuery('');
        if (serverSideSearchRef.current) onSearchChangeRef.current?.('');
      }
    }
    document.addEventListener('dropdown:opened', handleOtherDropdownOpened);
    return () => {
      document.removeEventListener(
        'dropdown:opened',
        handleOtherDropdownOpened,
      );
    };
  }, [instanceId]);

  useEffect(() => {
    if (isOpen && searchable) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
    if (!isOpen) {
      setSearchQuery('');
      didAutoScrollRef.current = false;
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!serverSideSearch) return undefined;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      onSearchChangeRef.current?.(searchQuery.trim());
    }, searchDebounceMs);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchQuery, serverSideSearch, searchDebounceMs]);

  const optionsCount = filteredOptions?.length || 0;

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();

      if (rect.width === 0 && rect.height === 0) return;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Estimate required height for the dropdown items
      const itemCount = optionsCount || 1;
      const estimatedItemHeight = 40;
      const searchHeight = searchable ? 52 : 0;
      const requiredHeight = Math.min(
        250,
        itemCount * estimatedItemHeight + searchHeight + 16,
      );

      // Prefer opening below; only flip to top if space below is truly insufficient
      // and space above can fit the content without overflowing off the top of the viewport.
      let newPlacement = 'bottom';
      if (
        spaceBelow < requiredHeight &&
        spaceAbove > spaceBelow &&
        spaceAbove >= 120
      ) {
        newPlacement = 'top';
      }

      const isMobile = window.innerWidth < 640;
      const targetWidth = dropdownWidth
        ? Number(dropdownWidth)
        : isMobile
          ? rect.width
          : Math.max(rect.width, 160);

      const finalWidth = Math.min(targetWidth, window.innerWidth - 16);
      let finalLeft = align === 'right' ? rect.right - finalWidth : rect.left;

      if (finalLeft + finalWidth > window.innerWidth - 8) {
        finalLeft = Math.max(8, window.innerWidth - 8 - finalWidth);
      }
      if (finalLeft < 8) {
        finalLeft = 8;
      }

      const maxAvailableHeight =
        newPlacement === 'bottom'
          ? Math.max(80, spaceBelow - 12)
          : Math.max(80, spaceAbove - 12);

      const nextStyle =
        newPlacement === 'bottom'
          ? {
              position: 'fixed',
              left: finalLeft,
              width: finalWidth,
              top: Math.max(8, rect.bottom + 4),
              bottom: undefined,
              maxHeight: Math.min(250, maxAvailableHeight),
            }
          : {
              position: 'fixed',
              left: finalLeft,
              width: finalWidth,
              top: undefined,
              bottom: Math.max(8, window.innerHeight - rect.top + 4),
              maxHeight: Math.min(250, maxAvailableHeight),
            };

      setDropdownStyle((prev) => {
        if (
          prev.position === nextStyle.position &&
          prev.left === nextStyle.left &&
          prev.width === nextStyle.width &&
          prev.top === nextStyle.top &&
          prev.bottom === nextStyle.bottom &&
          prev.maxHeight === nextStyle.maxHeight
        ) {
          return prev;
        }
        return nextStyle;
      });
    };

    const onScroll = (event) => {
      // Don't close if scrolling inside the dropdown list itself
      if (
        dropdownRef.current &&
        (event.target === dropdownRef.current ||
          dropdownRef.current.contains(event.target))
      ) {
        return;
      }
      // Close dropdown if the page or any parent container scrolls
      setIsOpen(false);
      setSearchQuery('');
      if (serverSideSearchRef.current) onSearchChangeRef.current?.('');
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isOpen, dropdownWidth, align, optionsCount, searchable]);

  const optionRefs = useRef({});

  const selectedValues = multiple
    ? Array.isArray(value)
      ? value
      : value === '' || value == null
        ? []
        : [value]
    : [];

  const isValueSelected = (optValue) => {
    if (multiple) {
      return selectedValues.some(
        (v) => String(v).toLowerCase() === String(optValue).toLowerCase(),
      );
    }
    return String(value).toLowerCase() === String(optValue).toLowerCase();
  };

  // Auto-scroll to selected only once on open (not on every search/filter change).
  useEffect(() => {
    if (!isOpen || isLoading || didAutoScrollRef.current || searchable) return;
    if (!listRef.current) return;

    requestAnimationFrame(() => {
      if (!listRef.current) return;
      let targetEl = null;
      const selectedValueStr = String(value);
      const selectedOpt = filteredOptions.find(
        (opt) => String(opt.value) === selectedValueStr,
      );
      if (
        selectedOpt &&
        !selectedOpt.disabled &&
        optionRefs.current[selectedValueStr]
      ) {
        targetEl = optionRefs.current[selectedValueStr];
      }
      if (targetEl) {
        const container = listRef.current;
        const targetScrollTop =
          targetEl.offsetTop -
          container.clientHeight / 2 +
          targetEl.clientHeight / 2;
        container.scrollTo({ top: targetScrollTop, behavior: 'auto' });
        didAutoScrollRef.current = true;
      }
    });
  }, [isOpen, isLoading, searchable, value, filteredOptions]);

  const selectedOption = !multiple
    ? options?.find(
        (opt) =>
          String(opt.value).toLowerCase() === String(value).toLowerCase(),
      )
    : null;

  const selectedMultiOptions = multiple
    ? options?.filter((opt) =>
        selectedValues.some(
          (v) => String(v).toLowerCase() === String(opt.value).toLowerCase(),
        ),
      ) || []
    : [];

  const multiLabel =
    selectedMultiOptions.length === 0
      ? null
      : selectedMultiOptions.length <= 2
        ? selectedMultiOptions.map((o) => o.label).join(', ')
        : `${selectedMultiOptions.length} selected`;

  const isValueEmpty = multiple
    ? selectedValues.length === 0
    : value === '' || value == null;

  const hasValue = !isValueEmpty;

  const displayLabel = multiple
    ? multiLabel || placeholder
    : isValueEmpty && placeholder
      ? placeholder
      : selectedOption
        ? selectedOption.label
        : placeholder || (hasValue && options?.[0] ? options[0].label : '');

  const isMuted = multiple
    ? !multiLabel
    : isValueEmpty
      ? true
      : !selectedOption;

  const handleClear = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (onClear) {
      onClear();
    } else if (multiple) {
      onChange([]);
    } else {
      onChange('');
    }
    setSearchQuery('');
    setIsOpen(false);
    if (serverSideSearchRef.current) onSearchChangeRef.current?.('');
  };

  const handleOptionClick = (option) => {
    if (option.disabled) return;
    if (multiple) {
      const already = selectedValues.some(
        (v) => String(v).toLowerCase() === String(option.value).toLowerCase(),
      );
      const next = already
        ? selectedValues.filter(
            (v) =>
              String(v).toLowerCase() !== String(option.value).toLowerCase(),
          )
        : [...selectedValues, option.value];
      onChange(next);
      return;
    }
    onChange(option.value);
    setIsOpen(false);
    setSearchQuery('');
    if (serverSideSearch) onSearchChange('');
  };

  const handleListScroll = (e) => {
    if (!onLoadMore || !hasMore || isFetchingMore) return;
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 40) {
      onLoadMore();
    }
  };

  const defaultButtonStyles = `w-full h-full flex items-center justify-between rounded-xl bg-[#FFFFFF] border ${
    error
      ? 'border-status-error focus:border-status-error focus:ring-[3px] focus:ring-status-error/15'
      : isOpen
        ? 'border-[#50AB3D]'
        : 'border-border-default focus:border-[#50AB3D] focus:ring-[3px] focus:ring-[#50AB3D]/15'
  } text-text-primary font-sf-pro-display focus:outline-none transition-all px-3 sm:px-4 disabled:opacity-50 cursor-pointer ${textSizeClass}`;

  const handleToggleOpen = () => {
    const next = !isOpen;
    if (next) {
      document.dispatchEvent(
        new CustomEvent('dropdown:opened', { detail: { id: instanceId } }),
      );
    }
    setIsOpen(next);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        id={id}
        disabled={disabled || isLoading}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy || undefined}
        aria-labelledby={ariaLabelledBy || undefined}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleToggleOpen}
        className={`${buttonClassName || defaultButtonStyles} ${
          !buttonClassName?.includes('flex')
            ? 'flex items-center justify-between'
            : ''
        }`}
        style={buttonStyle}
      >
        {isLoading && !selectedOption && !(multiple && multiLabel) ? (
          <div className="h-4 w-24 bg-input-background rounded animate-pulse" />
        ) : (
          <span
            title={displayLabel}
            className={`min-w-0 flex-1 ${
              renderValue ? 'flex items-center gap-1.5' : 'truncate'
            } ${
              align === 'center'
                ? 'text-center'
                : align === 'right'
                  ? 'text-right'
                  : 'text-left'
            } ${isMuted ? 'text-text-muted' : ''}`}
          >
            {renderValue && selectedOption
              ? renderValue(selectedOption)
              : displayLabel}
          </span>
        )}
        {((clearable && !disabled && !isLoading && hasValue) ||
          !hideChevron) && (
          <div className="flex items-center shrink-0 ml-1">
            {clearable && !disabled && !isLoading && hasValue && (
              <span
                role="button"
                tabIndex={-1}
                title="Clear selection"
                aria-label="Clear selection"
                onClick={handleClear}
                className="p-1 rounded-full hover:bg-neutral-100 text-text-muted hover:text-text-primary transition-colors cursor-pointer mr-0.5 shrink-0 flex items-center justify-center"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </span>
            )}
            {!hideChevron && (
              <svg
                className={`w-4 h-4 flex-shrink-0 transition-transform text-[#50AB3D] ${isOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            )}
          </div>
        )}
      </button>

      {isOpen &&
        createPortal(
          <>
            {/* Mobile backdrop to block background scroll & interactions and close on tap */}
            <div
              className="fixed inset-0 z-[99998] bg-black/0 lg:hidden"
              onClick={() => {
                setIsOpen(false);
                setSearchQuery('');
                if (serverSideSearch) onSearchChange('');
              }}
              onTouchMove={(e) => {
                if (e.cancelable) e.preventDefault();
              }}
              onWheel={(e) => {
                if (e.cancelable) e.preventDefault();
              }}
              aria-hidden="true"
            />
            <div
              ref={dropdownRef}
              style={{ ...dropdownStyle, overscrollBehavior: 'contain' }}
              data-portal-dropdown="true"
              className="z-[99999] flex flex-col max-h-[250px] bg-[#FFFFFF] border border-border-default rounded-xl shadow-2xl overflow-hidden"
            >
              {searchable && (
                <div className="shrink-0 z-10 bg-[#FFFFFF] border-b border-border-default p-2">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder={searchPlaceholder}
                    className="w-full h-9 px-3 rounded-lg bg-[#F5F5F5] border border-transparent text-sm text-[#1F2241] placeholder:text-text-muted focus:outline-none focus:border-[#50AB3D]"
                  />
                </div>
              )}
              <div
                ref={listRef}
                style={{ overscrollBehavior: 'contain' }}
                className="flex-1 min-h-0 overflow-y-auto stage-column-scrollbar py-1 overscroll-contain"
                onScroll={handleListScroll}
              >
                {isLoading ? (
                  <div className="p-2 flex flex-col gap-1.5">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="h-8 w-full bg-input-background rounded-lg animate-pulse"
                      />
                    ))}
                  </div>
                ) : filteredOptions.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-text-muted text-center">
                    {searchable && searchQuery.trim()
                      ? 'No matches found'
                      : emptyMessage}
                  </div>
                ) : (
                  <>
                    {filteredOptions.map((option, optionIndex) => {
                      const isSelected = isValueSelected(option?.value);
                      const optKey =
                        option?.value != null
                          ? `${optionIndex}-${String(option.value)}`
                          : `opt-${optionIndex}`;
                      return (
                        <button
                          key={optKey}
                          ref={(el) => {
                            if (el && option?.value != null)
                              optionRefs.current[String(option.value)] = el;
                          }}
                          type="button"
                          disabled={option.disabled}
                          className={
                            renderOption
                              ? `w-full block px-2 py-1 focus:outline-none ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`
                              : `relative w-full flex items-center ${
                                  align === 'center'
                                    ? 'justify-center text-center'
                                    : align === 'right'
                                      ? 'justify-end text-right'
                                      : 'justify-start text-left'
                                } gap-2 px-4 py-2.5 font-sf-pro-display transition-colors cursor-pointer ${textSizeClass} ${
                                  option.disabled
                                    ? 'opacity-50 cursor-not-allowed text-text-muted'
                                    : isSelected
                                      ? 'bg-[#EBF4EA] text-[#50AB3D]'
                                      : 'text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D]'
                                }`
                          }
                          onClick={() => handleOptionClick(option)}
                        >
                          {renderOption ? (
                            renderOption(option, { isSelected })
                          ) : (
                            <>
                              {!hideCheckIcon && (
                                <span
                                  className={`${
                                    align === 'center'
                                      ? 'absolute left-3.5'
                                      : ''
                                  } w-4 h-4 flex-shrink-0 flex items-center justify-center`}
                                >
                                  {isSelected && (
                                    <svg
                                      className="w-4 h-4 text-[#50AB3D]"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                      viewBox="0 0 24 24"
                                      aria-hidden="true"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4.5 12.75l6 6 9-13.5"
                                      />
                                    </svg>
                                  )}
                                </span>
                              )}
                              <span
                                title={option.label}
                                className={`truncate ${
                                  align === 'center'
                                    ? 'text-center w-full'
                                    : align === 'right'
                                      ? 'text-right'
                                      : 'flex-1 min-w-0 text-left'
                                }`}
                              >
                                {option.label}
                              </span>
                            </>
                          )}
                        </button>
                      );
                    })}
                    {isFetchingMore && (
                      <div className="px-4 py-2 text-xs text-text-muted text-center">
                        Loading more...
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
